import { Link } from 'react-router-dom';
import {
  DeleteOutline,
  Edit,
  SearchOutlined,
  AddCircleOutline,
  QuestionAnswerOutlined,
  CategoryOutlined,
  KeyOutlined,
  FilterListOutlined,
} from '@mui/icons-material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { getAllCategoryQuestions } from '../../../../util/DatabaseUtil';

function Question() {
  const server_url = process.env.REACT_APP_API_URI;
  const [category, setCategory] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalQuestions: 0,
    totalCategories: 0,
  });
  const limit = 10;

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(
          `${server_url}/context/get-all-category`,
        );
        const categories = response.data.data;
        setCategory(categories);
        setStats((prev) => ({ ...prev, totalCategories: categories.length }));
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [server_url]);

  useEffect(() => {
    let filtered = [...questions];

    if (searchTerm) {
      filtered = filtered.filter(
        (q) =>
          q?.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q?.searchKey?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredQuestions(filtered);
  }, [searchTerm, questions]);

  const handleQuestionDelete = async (catId, questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?'))
      return;
    try {
      const response = await axios.post(
        `${server_url}/question/delete-question`,
        { catId, questionId },
      );
      alert(response.data.msg);
      if (response.data.status) getAllQuestion(catId, page);
    } catch (err) {
      console.error('Error deleting question:', err);
    }
  };

  const getAllQuestion = async (id, pageNumber = 1) => {
    try {
      setLoading(true);
      const data = await getAllCategoryQuestions(id, pageNumber, limit);
      setQuestions(data?.data || []);
      setFilteredQuestions(data?.data || []);
      setTotalPages(data?.totalPages || 1);
      setStats((prev) => ({
        ...prev,
        totalQuestions: data?.totalQuestions || 0,
      }));
    } catch (err) {
      console.error('Error fetching questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    getAllQuestion(selectedCategory, newPage);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Question Management
        </h1>
        <p className="text-gray-600">
          Manage rating questions across different categories
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Total Questions
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalQuestions}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <QuestionAnswerOutlined className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Categories</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalCategories}
              </h3>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <CategoryOutlined className="text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Category Selector & Add Button */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FilterListOutlined
                className="text-gray-500 mr-1"
                fontSize="small"
              />
              Select Category
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              value={selectedCategory}
              onChange={(e) => {
                const selectedId = e.target.value;
                setSelectedCategory(selectedId);
                setPage(1);
                setSearchTerm('');
                getAllQuestion(selectedId, 1);

                const selectedCat = category.find(
                  (cat) => cat._id === selectedId,
                );
                if (selectedCat) {
                  setSelectedCategoryName(selectedCat.title);
                } else {
                  setSelectedCategoryName('');
                }
              }}
            >
              <option value="" disabled>
                Choose a category to view questions
              </option>
              {category?.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.title}
                </option>
              ))}
            </select>
          </div>

          {selectedCategory && (
            <Link
              to="/admin/newQuestion"
              state={{
                catId: selectedCategory,
                catName: selectedCategoryName,
              }}
              className="w-full"
            >
              <button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg">
                <AddCircleOutline />
                Add New Question
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* Search Bar */}
      {selectedCategory && questions.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-4">
          <div className="relative">
            <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by question text or search key..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {/* Questions Table */}
      {selectedCategory && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                  <tr>
                    <th className="py-4 px-6 text-left font-semibold">#</th>
                    <th className="py-4 px-6 text-left font-semibold">
                      Question
                    </th>
                    <th className="py-4 px-6 text-left font-semibold">
                      Search Key
                    </th>
                    <th className="py-4 px-6 text-center font-semibold">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredQuestions.length > 0 ? (
                    filteredQuestions.map((question, index) => (
                      <tr
                        key={question._id}
                        className="hover:bg-blue-50 transition-colors"
                      >
                        <td className="py-4 px-6 text-gray-600 font-medium">
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-start gap-2">
                            <QuestionAnswerOutlined
                              className="text-blue-500 mt-1"
                              fontSize="small"
                            />
                            <span className="text-gray-800">
                              {question.question}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2">
                            <KeyOutlined
                              className="text-gray-400"
                              fontSize="small"
                            />
                            <span className="text-gray-600 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                              {question.searchKey}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center justify-center gap-2">
                            <Link
                              to="/admin/edit-questions/"
                              state={{
                                catId: selectedCategory,
                                questionId: question._id,
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                              title="Edit Question"
                            >
                              <Edit fontSize="small" />
                            </Link>
                            <button
                              onClick={() =>
                                handleQuestionDelete(
                                  selectedCategory,
                                  question._id,
                                )
                              }
                              className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                              title="Delete Question"
                            >
                              <DeleteOutline fontSize="small" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="py-12 text-center">
                        <div className="flex flex-col items-center gap-2">
                          <QuestionAnswerOutlined className="text-gray-400 text-5xl" />
                          <p className="text-gray-500 text-lg font-medium">
                            {searchTerm
                              ? 'No questions match your search'
                              : 'No questions found'}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {searchTerm
                              ? 'Try adjusting your search term'
                              : 'Click "Add New Question" to create one'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Empty State - No Category Selected */}
      {!selectedCategory && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <CategoryOutlined className="text-gray-400 text-6xl mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Select a Category
          </h3>
          <p className="text-gray-500">
            Choose a category from the dropdown above to view and manage
            questions
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && selectedCategory && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (page <= 3) {
                pageNumber = index + 1;
              } else if (page >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = page - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    page === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Question;
